import {StateCreator} from "zustand/vanilla";
import {
  ActivateDashboardVersionResponse,
  ArchiveDashboardResponse,
  CreateDashboardResponse,
  DashboardCurrent as Dashboard,
  DashboardVersion,
  GetDashboardResponse,
  ListDashboardVersionsResponse,
  ListDashboardsResponse,
  ReplaceDashboardResponse,
  RestoreDashboardResponse,
  RestoreDashboardVersionResponse,
  SetDashboardFavoriteRequest,
  SetDashboardFavoriteResponse,
  UpdateDashboardMetadataRequest,
  UpdateDashboardMetadataResponse,
} from "@devplaninc/codeup";
import {GenerateDashboardRequest} from "@/pb/api/devplan/services/web/company";
import {
  dashboardAPI,
  dashboardArchiveAPI,
  dashboardFavoriteAPI,
  dashboardMetadataAPI,
  dashboardRefreshAPI,
  dashboardRegenerateAPI,
  dashboardRestoreAPI,
  dashboardUpdateAPI,
  dashboardVersionActivateAPI,
  dashboardVersionRestoreAPI,
  dashboardVersionsAPI,
  dashboardsAPI,
} from "@/app/apiPaths";
import {jr} from "@/hooks/queries/response-handler";

export type DashboardCreateInput = {
  prompt: string;
};

export type DashboardRegenerateInput = {
  dashboardId: string;
  prompt?: string;
};

export type DashboardMetadataInput = {
  dashboardId: string;
  title: string;
};

export type DashboardVersionRestoreInput = {
  dashboardId: string;
  versionId: string;
};

export type DashboardFavoriteInput = {
  dashboardId: string;
  favorite: boolean;
};

export type DashboardsStoreState = {
  dashboards: Record<number, Record<string, Dashboard>>;
  dashboardVersions: Record<number, Record<string, DashboardVersion[]>>;
  fetchDashboards(companyId: number, archived?: boolean): Promise<Dashboard[]>;
  fetchDashboard(companyId: number, dashboardId: string): Promise<Dashboard>;
  createDashboard(companyId: number, input: DashboardCreateInput): Promise<Dashboard>;
  regenerateDashboard(companyId: number, input: DashboardRegenerateInput): Promise<Dashboard>;
  updateDashboard(companyId: number, input: DashboardRegenerateInput): Promise<Dashboard>;
  refreshDashboard(companyId: number, dashboardId: string): Promise<Dashboard>;
  updateDashboardMetadata(companyId: number, input: DashboardMetadataInput): Promise<Dashboard>;
  archiveDashboard(companyId: number, dashboardId: string): Promise<Dashboard>;
  restoreDashboard(companyId: number, dashboardId: string): Promise<Dashboard>;
  fetchDashboardVersions(companyId: number, dashboardId: string): Promise<DashboardVersion[]>;
  activateDashboardVersion(companyId: number, input: DashboardVersionRestoreInput): Promise<Dashboard>;
  restoreDashboardVersion(companyId: number, input: DashboardVersionRestoreInput): Promise<Dashboard>;
  setDashboardFavorite(companyId: number, input: DashboardFavoriteInput): Promise<Dashboard>;
};

export const createDashboardsSlice: StateCreator<
  DashboardsStoreState,
  [],
  [],
  DashboardsStoreState
> = (set) => ({
  dashboards: {},
  dashboardVersions: {},
  fetchDashboards: async (companyId, archived = false) => {
    const response = await fetch(`${dashboardsAPI(companyId)}?archived=${archived}`);
    const js = await jr(response);
    const {dashboards} = ListDashboardsResponse.fromJSON(js);
    set(s => ({
      ...s,
      dashboards: {
        ...s.dashboards,
        [companyId]: replaceDashboardList(s.dashboards[companyId], dashboards, archived),
      },
    }));
    return dashboards;
  },
  fetchDashboard: async (companyId, dashboardId) => {
    const response = await fetch(dashboardAPI(companyId, dashboardId));
    const js = await jr(response);
    const {dashboard} = GetDashboardResponse.fromJSON(js);
    if (!dashboard?.head?.id) {
      throw new Error("Dashboard response did not include a dashboard.");
    }
    setDashboard(set, companyId, dashboard, true);
    return dashboard;
  },
  createDashboard: async (companyId, input) => {
    const response = await fetch(dashboardsAPI(companyId), {
      method: "POST",
      body: JSON.stringify(GenerateDashboardRequest.toJSON(GenerateDashboardRequest.create({prompt: input.prompt}))),
    });
    const js = await jr(response);
    const {dashboard} = CreateDashboardResponse.fromJSON(js);
    if (!dashboard?.head?.id) {
      throw new Error("Dashboard response did not include a dashboard.");
    }
    setDashboard(set, companyId, dashboard, true);
    return dashboard;
  },
  regenerateDashboard: async (companyId, input) => {
    const response = await fetch(dashboardRegenerateAPI(companyId, input.dashboardId), {
      method: "POST",
      body: JSON.stringify(GenerateDashboardRequest.toJSON(GenerateDashboardRequest.create({prompt: input.prompt ?? ""}))),
    });
    const js = await jr(response);
    const {dashboard} = ReplaceDashboardResponse.fromJSON(js);
    if (!dashboard?.head?.id) {
      throw new Error("Dashboard response did not include a dashboard.");
    }
    setDashboard(set, companyId, dashboard);
    return dashboard;
  },
  updateDashboard: async (companyId, input) => {
    const response = await fetch(dashboardUpdateAPI(companyId, input.dashboardId), {
      method: "POST",
      body: JSON.stringify(GenerateDashboardRequest.toJSON(GenerateDashboardRequest.create({prompt: input.prompt ?? ""}))),
    });
    const js = await jr(response);
    const {dashboard} = ReplaceDashboardResponse.fromJSON(js);
    if (!dashboard?.head?.id) throw new Error("Dashboard response did not include a dashboard.");
    setDashboard(set, companyId, dashboard);
    return dashboard;
  },
  refreshDashboard: async (companyId, dashboardId) => {
    const response = await fetch(dashboardRefreshAPI(companyId, dashboardId), {method: "POST"});
    const js = await jr(response);
    const {dashboard} = ReplaceDashboardResponse.fromJSON(js);
    if (!dashboard?.head?.id) throw new Error("Dashboard response did not include a dashboard.");
    setDashboard(set, companyId, dashboard);
    return dashboard;
  },
  updateDashboardMetadata: async (companyId, input) => {
    const response = await fetch(dashboardMetadataAPI(companyId, input.dashboardId), {
      method: "PATCH",
      body: JSON.stringify(UpdateDashboardMetadataRequest.toJSON(UpdateDashboardMetadataRequest.create({
        workspaceId: String(companyId),
        dashboardId: input.dashboardId,
        title: input.title,
      }))),
    });
    const js = await jr(response);
    const {dashboard} = UpdateDashboardMetadataResponse.fromJSON(js);
    if (!dashboard?.head?.id) throw new Error("Dashboard response did not include a dashboard.");
    setDashboard(set, companyId, dashboard);
    return dashboard;
  },
  archiveDashboard: async (companyId, dashboardId) => {
    const response = await fetch(dashboardArchiveAPI(companyId, dashboardId), {method: "POST"});
    const js = await jr(response);
    const {dashboard} = ArchiveDashboardResponse.fromJSON(js);
    if (!dashboard?.head?.id) throw new Error("Dashboard response did not include a dashboard.");
    setDashboard(set, companyId, dashboard, true);
    return dashboard;
  },
  restoreDashboard: async (companyId, dashboardId) => {
    const response = await fetch(dashboardRestoreAPI(companyId, dashboardId), {method: "POST"});
    const js = await jr(response);
    const {dashboard} = RestoreDashboardResponse.fromJSON(js);
    if (!dashboard?.head?.id) throw new Error("Dashboard response did not include a dashboard.");
    setDashboard(set, companyId, dashboard, true);
    return dashboard;
  },
  fetchDashboardVersions: async (companyId, dashboardId) => {
    const response = await fetch(dashboardVersionsAPI(companyId, dashboardId));
    const js = await jr(response);
    const {versions} = ListDashboardVersionsResponse.fromJSON(js);
    set(s => ({
      ...s,
      dashboardVersions: {
        ...s.dashboardVersions,
        [companyId]: {
          ...(s.dashboardVersions[companyId] || {}),
          [dashboardId]: versions,
        },
      },
    }));
    return versions;
  },
  activateDashboardVersion: async (companyId, input) => {
    const response = await fetch(dashboardVersionActivateAPI(companyId, input.dashboardId, input.versionId), {method: "POST"});
    const js = await jr(response);
    const {dashboard} = ActivateDashboardVersionResponse.fromJSON(js);
    if (!dashboard?.head?.id) throw new Error("Dashboard response did not include a dashboard.");
    setDashboard(set, companyId, dashboard);
    return dashboard;
  },
  restoreDashboardVersion: async (companyId, input) => {
    const response = await fetch(dashboardVersionRestoreAPI(companyId, input.dashboardId, input.versionId), {method: "POST"});
    const js = await jr(response);
    const {dashboard} = RestoreDashboardVersionResponse.fromJSON(js);
    if (!dashboard?.head?.id) throw new Error("Dashboard response did not include a dashboard.");
    setDashboard(set, companyId, dashboard);
    return dashboard;
  },
  setDashboardFavorite: async (companyId, input) => {
    const response = await fetch(dashboardFavoriteAPI(companyId, input.dashboardId), {
      method: "PATCH",
      body: JSON.stringify(SetDashboardFavoriteRequest.toJSON(SetDashboardFavoriteRequest.create({
        dashboardId: input.dashboardId,
        favorite: input.favorite,
      }))),
    });
    const js = await jr(response);
    const {dashboard} = SetDashboardFavoriteResponse.fromJSON(js);
    if (!dashboard?.head?.id) throw new Error("Dashboard response did not include a dashboard.");
    setDashboard(set, companyId, dashboard, true);
    return dashboard;
  },
});

function replaceDashboardList(existing: Record<string, Dashboard> | undefined, dashboards: Dashboard[], archived: boolean) {
  const next = Object.fromEntries(Object.entries(existing || {}).filter(([, dashboard]) => (
    archived ? !dashboard.head?.archivedAt : Boolean(dashboard.head?.archivedAt)
  )));
  for (const dashboard of dashboards) {
    next[dashboardId(dashboard)] = dashboard;
  }
  return next;
}

function setDashboard(
  set: Parameters<StateCreator<DashboardsStoreState>>[0],
  companyId: number,
  dashboard: Dashboard,
  favoriteAuthoritative = false,
) {
  set(s => {
    const id = dashboardId(dashboard);
    const cachedFavorite = s.dashboards[companyId]?.[id]?.head?.favorite;
    const nextDashboard = !favoriteAuthoritative && cachedFavorite !== undefined && dashboard.head
      ? {...dashboard, head: {...dashboard.head, favorite: cachedFavorite}}
      : dashboard;
    return {
      ...s,
      dashboards: {
        ...s.dashboards,
        [companyId]: {
          ...(s.dashboards[companyId] || {}),
          [id]: nextDashboard,
        },
      },
    };
  });
}

function dashboardId(dashboard: Dashboard) {
  return dashboard.head?.id ?? "";
}
